import type * as React from 'react';
import { getPaginated } from '@Domain/Api';
import aiCostLimitUsersFetched, {
	type AiCostLimitUsersFetchedAction,
} from '@Application/AiCostLimits/Users/Actions/aiCostLimitUsersFetched';
import aiCostLimitUsersFetchFailed, {
	type AiCostLimitUsersFetchFailedAction,
} from '@Application/AiCostLimits/Users/Actions/aiCostLimitUsersFetchFailed';
import type FetchAiCostLimitUsers from '@Application/AiCostLimits/Users/Queries/FetchAiCostLimitUsers';
import type { UserPayload } from '@Application/Users/Users/Actions/usersFetched';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import parameters from '../../../parameters';

type Actions = AiCostLimitUsersFetchedAction | AiCostLimitUsersFetchFailedAction;

// `getPaginated` pushes subsequent pages into `results`, which therefore must be mutable.
type PaginatedUsers = {
	totalRecords: number;
	results: UserPayload[];
};

/**
 * Reads the account's users for the subject picker and the name lookup. Writes to
 * `aiCostLimitsStore.users`, never to `usersStore` (owned by the Users screen).
 */
export default (query: FetchAiCostLimitUsers) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			// The server refuses more than 200 rows per page; `page: 0` walks every page.
			const response = await getPaginated<PaginatedUsers>(
				`${parameters.api.host}/api/accounts/${query.accountId}/users?orderByLastName=ASC`,
				{ page: 0, rowsPerPage: 200 },
			);

			const action = aiCostLimitUsersFetched(response.results, query.accountId);
			dispatch(action);
			return action;
		} catch (error) {
			const action = aiCostLimitUsersFetchFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
